'use client'
import React from 'react'
import { useTranslations } from 'next-intl'
import ShellButton from '@/components/ShellButton'
import dynamic from 'next/dynamic'
import Image from 'next/image'

// Custom QuickAddButton with reversed colors
const CustomQuickAddButton = ({ productId, quantity, label }: { productId: string; quantity: number; label: string }) => {
  const handleAddToCart = () => {
    // We'll let the dynamically imported component handle the actual cart addition
    const button = document.querySelector(`button[data-product-id="${productId}"][data-quantity="${quantity}"]`) as HTMLButtonElement;
    if (button) button.click();
  };

  return (
    <button
      onClick={handleAddToCart}
      className="py-2 px-4 rounded-full text-sm font-medium transition-all duration-300 bg-text-main text-bg-main hover:bg-text-main/90"
      aria-label={label}
    >
      {label}
    </button>
  );
};

// Dynamically import the real QuickAddVariantButton (hidden but functional)
const QuickAddVariantButton = dynamic(
  () => import('@/components/products/QuickAddVariantButton'),
  { loading: () => <div className="h-10 w-full bg-primary/30 animate-pulse rounded-full"></div> }
)

interface Product {
  id: string;
  name: string;
  description?: string;
  slug: string;
  kg_price: boolean | number;
  product_image?: string;
  status?: string;
  price?: number;
  [key: string]: string | number | boolean | object | undefined;
}

interface FeaturedProductProps {
  featuredProduct: Product;
  locale: string;
}

export default function FeaturedProduct({ 
  featuredProduct, 
  locale 
}: FeaturedProductProps) {
  const t = useTranslations('articles')

  return (
    <section className="mt-12">
      <h2 className="text-3xl font-bold mb-4 text-primary">{t('featuredProduct.heading')}</h2>
      <div className="rounded-lg border border-border p-6 bg-surface/50">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Product Image */}
          {featuredProduct.product_image && (
            <div className="w-full md:w-1/3">
              <div className="relative w-full aspect-square rounded-lg overflow-hidden">
                <Image
                  src={featuredProduct.product_image}
                  alt={featuredProduct.name}
                  fill
                  sizes="(max-width: 768px) 100vw, 33vw"
                  className="object-cover"
                />
              </div>
            </div>
          )}
          
          {/* Product Content */}
          <div className={`flex flex-col gap-6 ${featuredProduct.product_image ? 'md:w-2/3' : 'w-full'}`}>
            <h3 className="text-2xl font-bold text-primary">{featuredProduct.name}</h3>
            <p className="text-lg text-text-secondary">{featuredProduct.description || ''}</p>
            
            {/* Quick add buttons */}
            <div className="flex items-center gap-3">
              {(() => {
                const isKgPrice = featuredProduct.kg_price !== false;
                
                const smallQuantity = isKgPrice ? 100 : 1;
                const largeQuantity = isKgPrice ? 1000 : 2;
                const sampleButton = isKgPrice 
                  ? '+100g' 
                  : t('featuredProduct.singleButton', { defaultValue: '1 Unit' });
                const kiloButton = isKgPrice 
                  ? '+1kg' 
                  : t('featuredProduct.multiButton', { defaultValue: '2 Units' });
                
                return (
                  <>
                    <div className="w-auto relative">
                      {/* Visible button with our custom styling */}
                      <CustomQuickAddButton 
                        productId={featuredProduct.id}
                        quantity={smallQuantity}
                        label={sampleButton}
                      />
                      {/* Hidden functional button */}
                      <div className="sr-only">
                        <QuickAddVariantButton
                          productId={featuredProduct.id}
                          quantity={smallQuantity}
                          label={sampleButton}
                          data-product-id={featuredProduct.id}
                          data-quantity={smallQuantity}
                        />
                      </div>
                    </div>
                    <div className="w-auto relative">
                      {/* Visible button with our custom styling */}
                      <CustomQuickAddButton 
                        productId={featuredProduct.id}
                        quantity={largeQuantity}
                        label={kiloButton}
                      />
                      {/* Hidden functional button */}
                      <div className="sr-only">
                        <QuickAddVariantButton
                          productId={featuredProduct.id}
                          quantity={largeQuantity}
                          label={kiloButton}
                          data-product-id={featuredProduct.id}
                          data-quantity={largeQuantity}
                        />
                      </div>
                    </div>
                  </>
                );
              })()}
              
              {/* View details button */}
              <div className="ml-auto">
                <ShellButton href={`/${locale}/products/${featuredProduct.slug}`} variant="white" className="text-center w-[180px] md:w-auto">
                  {t('featuredProduct.viewDetails')}
                </ShellButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
} 